// ============================================================================
//  courtGrid — czysta geometria JEDNEJ ciągłej siatki kafli (bez three.js/DOM).
//
//  Problem: każda strefa nawierzchni (kort, trumna, płyta/wybieg, obrzeże)
//  dostawała teksturę z `repeat` = wymiar/płytka, ale offsetem 0 — czyli siatkę
//  zakotwiczoną we własnej krawędzi. Działało tylko dla kortu (całkowita liczba
//  płytek). Trumna (wymiar regulaminowy, np. 3,60 m = ułamek płytek) miała
//  spoiny przesunięte względem kortu.
//
//  Rozwiązanie: kort jest referencją siatki. Każda strefa dostaje policzony
//  `offset`, tak by jej spoiny (całkowite współrzędne tekstury) trafiały w tę
//  samą siatkę świata co kort:
//    - oś X: spoiny na  worldX = -courtW/2 + k·tileM  (faza kortu, symetryczna)
//    - oś Z: spoiny na  worldZ = m·tileM              (faza 0, linia końcowa)
//
//  Mapowanie UV three.js (zweryfikowane dowodem — identyczne dla PlaneGeometry
//  obróconej o -90° i górnej ścianki BoxGeometry):
//    worldX = cx - w/2 + u·w      worldZ = cz + h/2 - v·h
//  three.js próbkuje teksturę jako  sampled = uv·repeat + offset.
// ============================================================================

// Repeat + offset tekstury strefy, by jej kratka pokrywała się z kratką kortu.
//   zone:  { cx, cz, w, h } — środek (świat) i wymiary płaszczyzny (X=w, Z=h)
//   ref:   { courtW, tileM } — szerokość kortu (faza X) i rozmiar płytki [m]
export function zoneTexTransform({ cx, cz, w, h }, { courtW, tileM }) {
  return {
    repeatX: w / tileM,
    repeatZ: h / tileM,
    // sampled_u = u·(w/tileM) + offsetX  ma być całkowite ⟺ worldX = -courtW/2 + k·tileM
    offsetX: (cx - w / 2 + courtW / 2) / tileM,
    // sampled_v = v·(h/tileM) + offsetZ  ma być całkowite ⟺ worldZ = m·tileM
    offsetZ: -(cz + h / 2) / tileM,
  }
}

// Rozmiar i środek (w Z) fizycznej płyty wokół kortu. Wybieg jest równy
// WSZĘDZIE dookoła — runoffM po bokach, z przodu I z tyłu (za linią końcową,
// Z<0). Żadnego minimum na maszt kosza: przy małym wybiegu (albo zerowym)
// słup kosza może stać poza płytą i to jest zamierzone (decyzja 25.08.2026).
//   courtW, courtL — wymiary kortu [m]; runoffM — wybieg [m]
// Zwraca { fullW, fullL, centerZ } [m]; centerZ liczone względem linii końcowej (Z=0).
export function plateExtent({ courtW, courtL, runoffM }) {
  return {
    fullW: courtW + 2 * runoffM,
    fullL: courtL + 2 * runoffM,
    centerZ: courtL / 2,
  }
}

// Wymiar ZEWNĘTRZNY boiska [m]: płytki (pole gry + wybieg z obu stron) plus
// obrzeże z obu stron — obrzeże leży zawsze NA ZEWNĄTRZ płytek, jak na
// layoutach produkcyjnych klienta (SES Battle 50×36 szt + wybieg 1 szt →
// 52×38 szt + 2×6,5 cm = 15,92×11,67 m). Wymiary boiska podajemy ZAWSZE z
// obrzeżem (uwaga klienta 04.09.2026) — jedno źródło dla rzutu 2D, odczytów
// pod stepperami, listy zapisów i payloadu wyceny.
//   widthTiles/lengthTiles — pole gry [szt]; runoffTiles — wybieg [szt];
//   tileMm — płytka [mm]; edgeMm — obrzeże [mm]
export function outerSize({ widthTiles, lengthTiles, runoffTiles = 0, tileMm, edgeMm = 0 }) {
  const tileM = tileMm / 1000
  const edgeM = edgeMm / 1000
  return {
    widthM: (widthTiles + 2 * runoffTiles) * tileM + 2 * edgeM,
    lengthM: (lengthTiles + 2 * runoffTiles) * tileM + 2 * edgeM,
  }
}

// Dociąga wymiary trumny (pola) do PEŁNYCH płytek siatki kortu — zawsze W DÓŁ
// (raczej mniejsza trumna niż większa), tak by oba brzegi trafiały w spoiny i
// trumna obejmowała wyłącznie całe płytki.
//   keyWTarget, keyLTarget — docelowe wymiary trumny [m] (regulaminowe, przycięte do obrysu)
//   tileM                  — rozmiar płytki [m]
//   widthTiles             — liczba płytek szerokości kortu (faza/parzystość spoin X)
// Zwraca { keyW, keyL } w metrach (wielokrotności tileM). Szerokość ma parzystość
// (w płytkach) zgodną z widthTiles — symetryczna trumna wokół X=0 trafia oboma
// brzegami w spoiny kortu (-courtW/2 + k·tileM) tylko przy zgodnej parzystości.
// Głębokość bez korekty parzystości: spoiny Z biegną od linii końcowej (faza 0).
export function snapKeyToGrid({ keyWTarget, keyLTarget, tileM, widthTiles }) {
  const eps = 1e-6 // tłumi błąd float, gdy cel jest dokładnie wielokrotnością płytki
  let wTiles = Math.floor(keyWTarget / tileM + eps)
  // parzystość zgodna z kortem → brzegi na spoinach; korekta zawsze w dół (–1 płytka)
  if ((wTiles - widthTiles) % 2 !== 0) wTiles -= 1
  wTiles = Math.max(0, wTiles)
  const lTiles = Math.max(0, Math.floor(keyLTarget / tileM + eps))
  return { keyW: wTiles * tileM, keyL: lTiles * tileM }
}

// Promień łuku (koło rzutów wolnych, no-charge) PROPORCJONALNY do realnej
// szerokości trumny. Trumna jest dociągana do spoin (snapKeyToGrid), więc
// regulaminowy stały promień wyglądał na małych boiskach jak „za małe półkole"
// (uwaga klientki 20.08.2026). Skala = keyW / spec.KEY_WIDTH — na pełnej
// trumnie FIBA daje regulaminowy promień bazowy.
//   keyW — realna szerokość trumny [m]; spec — LINES/LINES_FIBA; base — promień bazowy [m]
export function scaledArcRadius({ keyW, spec, base }) {
  return (keyW / spec.KEY_WIDTH) * base
}

// Czy linia za 3 pkt o zadanym promieniu jest NIEWYMIAROWA na tym obrysie —
// tzn. łuk ma REALNIE mniejszy promień, bo dociska go głębokość boiska
// (clamp w _threePt: R3 = min(radius, len - lineW - rimZ)). Przycięcie boczne
// na wąskim boisku NIE flaguje (fix 25.08.2026: pole gry 9×11 m było błędnie
// oznaczane): łuk kończy się wtedy na linii bocznej, ale wszędzie tam, gdzie
// istnieje, biegnie w regulaminowej odległości od kosza. Klient musi widzieć
// wprost tylko realne skrócenie promienia (uwaga 20.08.2026).
//   len — głębokość obrysu [m]; rimZ — środek obręczy od linii końcowej [m];
//   radius — promień łuku [m]; lineW — szerokość malowanej linii [m]
export function threePtUndersized({ len, rimZ, radius, lineW }) {
  return (len - lineW - rimZ) < radius
}

// Znaczniki miejsc do zbiórki przy trumnie — układ wg FIBA official 2020
// (diagram z materiałów klienta, docs/poprawki/poprawki_20.08.2026): od linii
// końcowej wąski znacznik na 1,75 m, BLOK „neutral zone" 0,40 m od 2,60 m,
// potem wąskie na 3,85 m i 4,70 m — razem „3 wąskie + 1 szeroki" (uwaga
// klienta 25.08.2026). Suwak kosza przesuwa trumnę, więc znaczniki dostają ten
// sam offset. Znacznik wchodzi tylko gdy mieści się W CAŁOŚCI przed linią RW
// (koniec ≤ ftZ - 0,1) i nie wpada w linię końcową (z ≥ 0,2).
//   ftZ — głębokość trumny / linia RW [m]; offsetM — przesunięcie kosza [m]
// Zwraca [{ z, depth }]: z — początek od linii końcowej [m]; depth — głębokość
// bloku [m] (0 = wąska kreska o szerokości linii malowanej).
export function laneTicks({ ftZ, offsetM = 0 }) {
  const base = [
    { z: 1.75, depth: 0 },
    { z: 2.6, depth: 0.4 },
    { z: 3.85, depth: 0 },
    { z: 4.7, depth: 0 },
  ]
  return base
    .map(({ z, depth }) => ({ z: z + offsetM, depth }))
    .filter(({ z, depth }) => z >= 0.2 && z + depth <= ftZ - 0.1)
}

// Współrzędne linii siatki spoin w obrębie prostokąta płyty (dla nakładki 2D).
// Faza identyczna z teksturą: X zakotwiczone w siatce kortu (-courtW/2 + k·tileM),
// Z w linii końcowej (m·tileM). Zwraca pełne linie kafli mieszczące się w płycie
// (na obwodzie naturalnie zostają płytki niepełne — siatka ich nie dorysowuje).
//   rect: { minX, maxX, minZ, maxZ } — zasięg fizycznej płyty (świat)
//   ref:  { courtW, tileM }
export function tileGridLines({ minX, maxX, minZ, maxZ }, { courtW, tileM }) {
  const eps = tileM * 1e-6
  const phaseX = -courtW / 2 // X = phaseX + k·tileM
  const lines = (phase, lo, hi) => {
    const kMin = Math.ceil((lo - phase) / tileM - eps)
    const kMax = Math.floor((hi - phase) / tileM + eps)
    const out = []
    for (let k = kMin; k <= kMax; k++) out.push(phase + k * tileM)
    return out
  }
  return {
    xs: lines(phaseX, minX, maxX),
    zs: lines(0, minZ, maxZ),
  }
}
